import { useEffect, useRef, useState, useCallback } from "react";
import { useTheme } from "@mui/material";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
const UK_CENTER: [number, number] = [-1.895662, 52.489706];
const INITIAL_ZOOM = 6;

const getMapStyle = (isDarkMode: boolean): string => {
  return isDarkMode
    ? "mapbox://styles/mapbox/dark-v11"
    : "mapbox://styles/mapbox/light-v11";
};

export const useMapInstance = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const theme = useTheme();
  const previousThemeModeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!mapContainer.current || mapRef.current || !MAPBOX_ACCESS_TOKEN) {
      if (!MAPBOX_ACCESS_TOKEN) {
        setError("MapBox access token is not configured");
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    setError(null);
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

    try {
      const mapStyle = getMapStyle(theme.palette.mode === "dark");

      const mapInstance = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapStyle,
        center: UK_CENTER,
        zoom: INITIAL_ZOOM,
        trackResize: true,
        minZoom: 6,
      });

      mapInstance.addControl(new mapboxgl.NavigationControl(), "top-left");

      const handleLoad = () => {
        setIsLoading(false);
        setError(null);
      };

      const handleError = (e: unknown) => {
        console.error("MapBox error:", e);
        setError("Failed to load map");
        setIsLoading(false);
      };

      mapInstance.on("load", handleLoad);
      mapInstance.on("error", handleError);

      mapRef.current = mapInstance;
      setMap(mapInstance);
      previousThemeModeRef.current = theme.palette.mode;

      return () => {
        mapInstance.off("load", handleLoad);
        mapInstance.off("error", handleError);
        mapInstance.remove();
        mapRef.current = null;
        setMap(null);
      };
    } catch (err) {
      console.error("MapBox initialization error:", err);
      setError("Failed to initialize map");
      setIsLoading(false);
    }
  }, [theme.palette.mode]);

  useEffect(() => {
    if (!map || !map.isStyleLoaded()) return;

    const currentThemeMode = theme.palette.mode;
    if (previousThemeModeRef.current === null) {
      previousThemeModeRef.current = currentThemeMode;
      return;
    }

    if (previousThemeModeRef.current !== currentThemeMode) {
      const mapStyle = getMapStyle(currentThemeMode === "dark");
      map.setStyle(mapStyle);
      previousThemeModeRef.current = currentThemeMode;
    }
  }, [map, theme.palette.mode]);

  useEffect(() => {
    const container = mapContainer.current;

    if (!container || !map) return;

    let rafId: number | undefined;

    const handleResize = () => {
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        map.resize();
      });
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [map]);

  const flyTo = useCallback(
    (
      center: [number, number],
      zoom: number,
      offset: [number, number] = [0, 0],
      duration: number = 1000
    ) => {
      if (!map) return;
      map.flyTo({
        center,
        zoom,
        offset,
        duration,
      });
    },
    [map]
  );

  return {
    mapContainer,
    map,
    isLoading,
    error,
    flyTo,
  };
};

